import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Book, MessageCircle, Activity, Clock, Users, Headphones, ChevronDown, ChevronUp } from 'lucide-react';
import SharedNavigation from './SharedNavigation';
import './SharedStyles.css';

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{ 
      border: '1px solid var(--border-light)', 
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '1.5rem',
          background: 'var(--background-white)',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '1.1rem',
          fontWeight: '600',
          color: 'var(--text-dark)',
          transition: 'background 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-light)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'var(--background-white)'}
      >
        <span>{question}</span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {isOpen && (
        <div style={{
          padding: '1.5rem',
          background: 'var(--background-light)',
          borderTop: '1px solid var(--border-light)',
          color: 'var(--text-medium)',
          lineHeight: '1.6'
        }}>
          {answer}
        </div>
      )}
    </div>
  );
};

const Support: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="app-page">
      <SharedNavigation />

      <main className="app-main">
        <div className="app-container">
          <div className="text-center mb-6">
            <h1 className="app-title">Support & Hilfe</h1>
            <p className="app-subtitle">
              Wir sind hier, um Ihnen bei der Integration und Nutzung der Pfändungstabelle API zu helfen
            </p>
          </div>
          
          <div className="app-card-grid">
            <div className="app-card text-center">
              <div style={{ 
                width: '60px', 
                height: '60px', 
                background: 'var(--primary-medium)', 
                borderRadius: '8px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 1.5rem',
                color: 'white'
              }}>
                <Mail size={24} />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                E-Mail Support
              </h3>
              <p style={{ color: 'var(--text-medium)', marginBottom: '1.5rem' }}>
                Für allgemeine Fragen und technischen Support
              </p>
              <a 
                href="mailto:support@voidmind.io" 
                className="app-button app-button-primary"
                style={{ textDecoration: 'none' }}
              >
                support@voidmind.io
              </a>
            </div>

            <div className="app-card text-center">
              <div style={{ 
                width: '60px', 
                height: '60px', 
                background: 'var(--primary-medium)', 
                borderRadius: '8px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 1.5rem',
                color: 'white'
              }}>
                <Book size={24} />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                API Dokumentation
              </h3>
              <p style={{ color: 'var(--text-medium)', marginBottom: '1.5rem' }}>
                Vollständige Dokumentation und Code-Beispiele
              </p>
              <Link 
                to="/#docs" 
                className="app-button app-button-primary"
                style={{ textDecoration: 'none' }}
              >
                Zur Dokumentation
              </Link>
            </div>

            <div className="app-card text-center">
              <div style={{ 
                width: '60px', 
                height: '60px', 
                background: 'var(--primary-medium)', 
                borderRadius: '8px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 1.5rem',
                color: 'white'
              }}>
                <MessageCircle size={24} />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                FAQ
              </h3>
              <p style={{ color: 'var(--text-medium)', marginBottom: '1.5rem' }}>
                Häufig gestellte Fragen und Antworten
              </p>
              <div className="app-button app-button-secondary" style={{ cursor: 'default' }}>
                Bald verfügbar
              </div>
            </div>

            <div className="app-card text-center">
              <div style={{ 
                width: '60px', 
                height: '60px', 
                background: 'var(--primary-medium)', 
                borderRadius: '8px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 1.5rem',
                color: 'white'
              }}>
                <Activity size={24} />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                Status
              </h3>
              <p style={{ color: 'var(--text-medium)', marginBottom: '1.5rem' }}>
                Aktuelle Verfügbarkeit und Systemstatus
              </p>
              <div 
                className="app-button" 
                style={{ 
                  background: 'var(--success-green)', 
                  color: 'white',
                  cursor: 'default'
                }}
              >
                99.9% Uptime
              </div>
            </div>
          </div>

          {/* Support Levels */}
          <div className="app-content" style={{ marginTop: '3rem' }}>
            <h2 style={{ marginBottom: '2rem' }}>Support-Level nach Plan</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
              <div style={{ 
                padding: '2rem', 
                border: '2px solid var(--border-light)', 
                borderRadius: '12px',
                background: 'var(--background-light)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <Clock size={24} style={{ color: 'var(--voidmind-primary)' }} />
                  <h3 style={{ margin: 0, color: 'var(--text-dark)' }}>Starter & Professional</h3>
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  <li>E-Mail-Support</li>
                  <li>Antwort innerhalb 24h</li>
                  <li>API-Dokumentation</li>
                  <li>Community-Forum</li>
                </ul>
              </div>

              <div style={{ 
                padding: '2rem', 
                border: '2px solid var(--voidmind-primary)', 
                borderRadius: '12px',
                background: 'rgba(99, 102, 241, 0.05)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <Headphones size={24} style={{ color: 'var(--voidmind-primary)' }} />
                  <h3 style={{ margin: 0, color: 'var(--text-dark)' }}>Business</h3>
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  <li>Prioritäts-E-Mail-Support</li>
                  <li>Telefon-Support</li>
                  <li>Antwort innerhalb 4h</li>
                  <li>Technische Beratung</li>
                </ul>
              </div>

              <div style={{ 
                padding: '2rem', 
                border: '2px solid var(--text-dark)', 
                borderRadius: '12px',
                background: 'rgba(30, 41, 59, 0.05)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <Users size={24} style={{ color: 'var(--text-dark)' }} />
                  <h3 style={{ margin: 0, color: 'var(--text-dark)' }}>Enterprise</h3>
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  <li>Dedicated Account Manager</li>
                  <li>24/7 Premium-Support</li>
                  <li>Custom Integrations</li>
                  <li>SLA-Garantien</li>
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="app-content" style={{ marginTop: '3rem' }}>
            <h2>Häufig gestellte Fragen</h2>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <FAQItem 
                question="Wie schnell erhalte ich Antwort auf meine Support-Anfrage?"
                answer="Die Antwortzeiten variieren je nach Plan: Starter/Professional innerhalb 24h, Business innerhalb 4h, Enterprise mit 24/7 Support."
              />
              <FAQItem 
                question="Bieten Sie Hilfe bei der API-Integration?"
                answer="Ja! Wir unterstützen Sie gerne bei der Integration. Business+ Kunden erhalten zusätzlich technische Beratung und Custom Integration Support."
              />
              <FAQItem 
                question="Was passiert bei API-Ausfällen?"
                answer="Wir garantieren 99.9% Uptime. Bei Ausfällen informieren wir proaktiv und Enterprise-Kunden erhalten automatische Kompensation gemäß SLA."
              />
              <FAQItem 
                question="Kann ich meinen Plan jederzeit ändern?"
                answer="Ja, Sie können jederzeit upgraden. Downgrades sind zum Ende des Abrechnungszeitraums möglich. Kontaktieren Sie uns für Details."
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Support;
